import { Icon } from "@twinkltech/curriculum-architect-component-library";
import { IconNavigationArrowDown, Text } from "@twinkltech/web-ui";
import React, { ComponentPropsWithoutRef, ElementType, useId } from "react";
import { useTranslation } from "react-i18next";

import { TAG_NS } from "./Tag.i18n";

export type TagVisibility = "private" | "school";

export type TagStatus = "active" | "archived";

export type TagPlanningStatus =
  | "planned"
  | "not_yet_planned"
  | "coming-soon"
  | "planning_in_progress";

type TagVariant = TagStatus | TagPlanningStatus | TagVisibility;

type TagProps<E extends ElementType = "span"> = {
  as?: E;
  asTrigger?: boolean;
  variant?: TagVariant;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
} & Omit<
  ComponentPropsWithoutRef<E>,
  "as" | "asTrigger" | "variant" | "className"
>;

export const Tag = <E extends ElementType = "span">({
  as,
  asTrigger = false,
  children,
  className,
  disabled,
  variant = "active",
  ...props
}: TagProps<E>) => {
  const Component = as || "span";
  const labelId = useId();

  const getVariantStyles = (variant: TagVariant) => {
    switch (variant) {
      case "active":
      case "planned":
        return "bg-green-50 border-green-100";

      case "private":
      case "school":
        return "bg-white-100 border-neutral-300";

      case "coming-soon":
        return "bg-orange-100 border-neutral-300";

      case "planning_in_progress":
        return "bg-blue-50 border-blue-100";

      case "not_yet_planned":
        return "bg-neutral-100 border-neutral-300";

      default:
        return "bg-neutral-100 border-neutral-300";
    }
  };

  const { t } = useTranslation(TAG_NS);

  const icon = {
    active: null,
    archived: null,
    planned: null,
    not_yet_planned: null,
    planning_in_progress: null,
    "coming-soon": null,
    private: <Icon icon="VisibilityOff" />,
    school: <Icon icon="SourceEnvironment" />,
  };

  return (
    <Component
      aria-labelledby={labelId}
      className={[
        "inline-flex items-center gap-050 border px-2 py-1 rounded-lg whitespace-nowrap",
        getVariantStyles(variant),
        className,
        disabled ? "opacity-50 !cursor-not-allowed" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      disabled={disabled}
      role={as ? undefined : "note"}
      {...props}
    >
      {icon[variant]}
      <Text as="span" className="flex items-center align-middle" id={labelId}>
        {children || t(variant)}
      </Text>
      {asTrigger && <IconNavigationArrowDown />}
    </Component>
  );
};
